/**
 * Browser-local agent loop using Chrome Built-in AI (Gemini Nano) +
 * the agent tool registry.
 *
 * Returns the same { reply, tool_results, provider_used } shape that the
 * existing server flow returns, so the AIAssistant UI is unchanged.
 *
 * Because Gemini Nano doesn't have native function calling, tool calls
 * are negotiated via a single-line convention the model is instructed to
 * emit: <<TOOL: tool_name {"arg":"value"}>>
 * The loop executes the tool, feeds the JSON result back, and continues
 * for up to MAX_ITERATIONS turns before falling through to a plain reply.
 */
import { createBrowserSession, type BrowserAISession } from './browserProvider'
import { describeToolsForPrompt, executeTool, registerTools } from '@/lib/agent/tools'

const MAX_ITERATIONS = 3

const SYSTEM_PROMPT = `You are the SaaSFarm admin assistant running locally on the admin's device via Chrome Built-in AI.

You help the admin understand their catalog, orders, treasury, and pricing. You run entirely in the browser — you cannot write, publish, or delete anything. Only read-only tools are available.

When you need fresh data, emit ONE line in this exact format and nothing else on that line:
<<TOOL: tool_name {"arg":"value"}>>

After the tool runs, the next user message will start with "TOOL_RESULT:" followed by JSON. Use that data to answer.

Available tools:
${describeToolsForPrompt()}

Rules:
- One tool call per turn.
- Keep final answers under 150 words.
- If the admin's question doesn't need data, just answer directly — no tool call.`

interface AgentTurn {
  reply: string
  tool_results: Array<{
    tool: string
    result: { success: boolean; data?: unknown; error?: string }
  }>
  provider_used: string
}

interface ApiMessage {
  role: 'user' | 'assistant'
  content: string
}

let cachedSession: BrowserAISession | null = null

async function getSession(): Promise<BrowserAISession> {
  if (!cachedSession) {
    cachedSession = await createBrowserSession(SYSTEM_PROMPT)
    registerTools()
  }
  return cachedSession
}

export function resetBrowserAgent(): void {
  try {
    cachedSession?.destroy()
  } catch {
    /* noop */
  }
  cachedSession = null
}

const TOOL_CALL_RE = /<<\s*TOOL:\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*(\{[\s\S]*?\})?\s*>>/

function stripToolCall(text: string): string {
  return text.replace(TOOL_CALL_RE, '').trim()
}

export async function runBrowserAgent(apiMessages: ApiMessage[]): Promise<AgentTurn> {
  let session = await getSession()
  const latest = apiMessages[apiMessages.length - 1]
  if (!latest || latest.role !== 'user') {
    throw new Error('No user message to respond to')
  }

  const toolResults: AgentTurn['tool_results'] = []
  let prompt = latest.content
  let reply = ''
  let retriedOnce = false

  for (let i = 0; i < MAX_ITERATIONS; i++) {
    let raw: string
    try {
      raw = await session.prompt(prompt)
    } catch (err) {
      // Session may be dead (e.g. context overflow) — reset and retry once per turn
      if (!retriedOnce) {
        retriedOnce = true
        resetBrowserAgent()
        session = await getSession()
        raw = await session.prompt(prompt)
      } else {
        throw err
      }
    }

    const match = raw.match(TOOL_CALL_RE)
    if (!match) {
      reply = raw.trim()
      break
    }

    const toolName = match[1]
    let args: Record<string, unknown> = {}
    if (match[2]) {
      try {
        const parsed = JSON.parse(match[2]) as unknown
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          args = parsed as Record<string, unknown>
        }
      } catch {
        /* malformed args — run tool with no args */
      }
    }

    const result = await executeTool(toolName, args)
    toolResults.push({ tool: toolName, result })

    // Keep any non-tool prose from the same turn as partial reply context
    const prose = stripToolCall(raw)
    if (prose && !reply) reply = prose

    // Trim tool output so Nano's small context window doesn't overflow
    const resultJson = JSON.stringify(result).slice(0, 1800)
    prompt = `TOOL_RESULT: ${resultJson}`

    if (i === MAX_ITERATIONS - 1) {
      try {
        const followup = await session.prompt(prompt)
        const finalText = stripToolCall(followup).trim()
        if (finalText) reply = finalText
      } catch {
        /* keep whatever reply we have */
      }
    }
  }

  return {
    reply: reply || 'Done.',
    tool_results: toolResults,
    provider_used: 'browser-gemini-nano',
  }
}
